import React, { useState } from 'react';
import { getVideoChips } from '../../utils/chipColors';

const styles = {
  carCard: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: '16px',
    overflow: 'hidden',
    width: '320px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
  },
  carHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    padding: '12px 16px 0 16px',
  },
  carNumber: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  playButton: {
    background: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    transition: 'background 0.2s',
    boxShadow: '0 2px 6px rgba(25, 118, 210, 0.10)',
  },
  playButtonHover: {
    background: '#1251a3',
  },
  carDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '0 16px 16px 16px',
  },
  carInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.875rem',
  },
  chipContainer: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '12px',
    marginBottom: '4px',
    paddingLeft: '2px',
  },
};

export default function CarSessionCard({
  car,
  session,
  group,
  onPlay,
  isMobile,
  viewMode,
  cardWidth = '320px',
  bestLap,
  totalLaps,
}) {
  const chips = getVideoChips(car);
  const [isHovered, setIsHovered] = useState(false);
  const driverName = car.drivername || car.username || 'Unknown Driver';
  const carNum = car.carNum ? `Car #${car.carNum}` : '';
  const trackVenue = group?.eventVenue || car?.eventVenue || '';
  const trackConfig = session?.trackConfig || car?.trackConfig || '';

  // Format best lap time (assume seconds)
  function formatLapTime(sec) {
    if (!sec || isNaN(sec)) return '--';
    const min = Math.floor(sec / 60);
    const s = (sec % 60).toFixed(3).padStart(6, '0');
    return `${min}:${s}`;
  }

  return (
    <div
      style={{
        ...styles.carCard,
        width: cardWidth,
        ...(isMobile ? { width: '100%' } : {}),
      }}
    >
      <div style={styles.carHeader}>
        <div
          style={{ ...styles.carNumber, fontSize: '1.15rem', fontWeight: 600 }}
        >
          {driverName}
          <div
            style={{
              fontSize: '0.75rem',
              color: '#888',
              fontFamily: 'monospace',
              marginTop: 2,
            }}
          >
            ID: {car.id}
          </div>
        </div>
        <button
          style={{
            ...styles.playButton,
            ...(isHovered ? styles.playButtonHover : {}),
          }}
          onClick={onPlay}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label={`Play ${carNum}`}
        >
          ▶
        </button>
      </div>

      <div style={styles.carDetails}>
        {carNum && (
          <div style={styles.carInfo}>
            <span>🚗</span>
            <span>{carNum}</span>
          </div>
        )}
        <div style={styles.carInfo}>
          <span>📹</span>
          <span>{car.cameraAngle || 'Unknown Angle'}</span>
        </div>
        {trackVenue && (
          <div style={styles.carInfo}>
            <span>🏟️</span>
            <span>{trackVenue}</span>
          </div>
        )}
        {trackConfig && (
          <div style={styles.carInfo}>
            <span>⚙️</span>
            <span>{trackConfig}</span>
          </div>
        )}
        {!isMobile && viewMode === 'track' && (
          <div style={styles.carInfo}>
            <span>📅</span>
            <span>{group.eventName}</span>
          </div>
        )}
        {car.auditStatus && (
          <div style={styles.carInfo}>
            <span>🔍</span>
            <span>Audit: {car.auditStatus}</span>
          </div>
        )}
        {(bestLap || totalLaps) && (
          <div style={{ ...styles.carInfo, marginTop: 4 }}>
            {bestLap && (
              <span style={{ marginRight: 12 }}>
                🏁 Best: {formatLapTime(bestLap)}
              </span>
            )}
            {totalLaps && <span>🔢 Laps: {totalLaps}</span>}
          </div>
        )}

        <div style={styles.chipContainer}>
          {chips.map((chip, index) => (
            <span key={index} style={chip.style}>
              {chip.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
